import React, { useState } from 'react';
import { useNotifications } from '../../hooks/useRealTime';
import {
  BellIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, dismissNotification } = useNotifications();

  const getNotificationIcon = (type) => {
    const iconClass = "h-6 w-6";
    
    switch (type) {
      case 'new_order':
        return <CheckCircleIcon className={`${iconClass} text-green-500`} />;
      case 'low_stock':
        return <ExclamationTriangleIcon className={`${iconClass} text-yellow-500`} />;
      case 'new_customer':
        return <InformationCircleIcon className={`${iconClass} text-blue-500`} />;
      case 'system':
        return <ClockIcon className={`${iconClass} text-gray-500`} />;
      default:
        return <BellIcon className={`${iconClass} text-gray-500`} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-r-4 border-red-500';
      case 'medium':
        return 'border-r-4 border-yellow-500';
      case 'low':
        return 'border-r-4 border-blue-500';
      default:
        return 'border-r-4 border-gray-300';
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `לפני ${days} ימים`;
    if (hours > 0) return `לפני ${hours} שעות`;
    if (minutes > 0) return `לפני ${minutes} דקות`;
    return 'כרגע';
  };

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-full"
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="absolute left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-40 max-h-96 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">התראות</h3>
                <div className="flex items-center space-x-2">
                  {unreadCount > 0 && (
                    <span className="text-sm text-gray-500">
                      {unreadCount} לא נקראו
                    </span>
                  )}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-gray-500 p-1 rounded"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <BellIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">אין התראות חדשות</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-6 py-4 hover:bg-gray-50 cursor-pointer ${
                        !notification.read ? 'bg-primary-50' : ''
                      } ${getPriorityColor(notification.priority)}`}
                      onClick={() => markAsRead(notification.id)}
                    >
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 mt-1">
                          {getNotificationIcon(notification.type)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className={`text-sm font-medium ${
                              !notification.read ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {notification.title}
                            </p>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">
                                {formatTime(notification.timestamp)}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dismissNotification(notification.id);
                                }}
                                className="text-gray-400 hover:text-gray-500 p-1"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          
                          <p className={`text-sm ${
                            !notification.read ? 'text-gray-700' : 'text-gray-500'
                          }`}>
                            {notification.message}
                          </p>
                          
                          {/* Action buttons for certain notification types */}
                          {notification.type === 'new_order' && (
                            <div className="mt-3 flex space-x-2">
                              <button className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded hover:bg-primary-200">
                                צפה בהזמנה
                              </button>
                              <button className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200">
                                אשר הזמנה
                              </button>
                            </div>
                          )}
                          
                          {notification.type === 'low_stock' && (
                            <div className="mt-3">
                              <button className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded hover:bg-yellow-200">
                                עדכן מלאי
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Unread indicator */}
                        {!notification.read && (
                          <div className="flex-shrink-0">
                            <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => {
                      notifications.forEach(notif => {
                        if (!notif.read) markAsRead(notif.id);
                      });
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    סמן הכל כנקרא
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-sm text-gray-600 hover:text-gray-700"
                  >
                    צפה בכל ההתראות
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Toast Notification Component
 * For temporary notifications that appear and auto-dismiss
 */
export const ToastNotification = ({ notification, onDismiss }) => {
  if (!notification) return null;

  const getToastColors = (type) => {
    switch (type) {
      case 'success':
        return 'bg-green-100 border-green-400 text-green-700';
      case 'error':
        return 'bg-red-100 border-red-400 text-red-700';
      case 'warning':
        return 'bg-yellow-100 border-yellow-400 text-yellow-700';
      case 'info':
        return 'bg-blue-100 border-blue-400 text-blue-700';
      default:
        return 'bg-gray-100 border-gray-400 text-gray-700';
    }
  };

  return (
    <div className={`fixed top-4 left-4 max-w-sm w-full border-r-4 p-4 rounded-lg shadow-lg z-50 ${getToastColors(notification.type)}`}>
      <div className="flex items-start">
        <div className="flex-1">
          <p className="text-sm font-medium">{notification.title}</p>
          {notification.message && (
            <p className="text-sm mt-1">{notification.message}</p>
          )}
        </div>
        <button
          onClick={onDismiss}
          className="flex-shrink-0 mr-4 text-current hover:text-opacity-75"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default NotificationCenter;
